// Ejemplo de deepGet/deepSet para objetos complejos
import {Partner, ServiceOrderTemplate, TableColumn} from '@app/shared';
import {ServicePricing} from '../../shared/models/service-pricing';

export function deepGet(obj: any, path: string): any {
  return path.split('.').reduce((o, p) => o?.[p], obj);
}

export function deepSet(obj: any, path: string, value: any) {
  const keys = path.split('.');
  const lastKey = keys.pop()!;
  const target = keys.reduce((o, p) => o[p] = o[p] || {}, obj);
  target[lastKey] = value;
}

/** Se utiliza para clonar objetos complejos y eliminar la referencia y evitar el double binding **/
export function deepClone<T>(obj: T): T {
  // Caso base: si no es un objeto, devolver el valor directamente
  if (obj === null || typeof obj !== 'object') {
    return obj;
  }

  // Manejar arrays
  if (Array.isArray(obj)) {
    return obj.map(item => deepClone(item)) as unknown as T;
  }

  // Manejar objetos
  const clonedObj = {} as T;
  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      clonedObj[key] = deepClone(obj[key]);
    }
  }

  return clonedObj;
}

export function deepCompare(obj1: any, obj2: any): boolean {
  // Caso 1: Comparación directa para tipos primitivos
  if (obj1 === obj2) return true;

  // Caso 2: Comparación de tipos
  if (typeof obj1 !== typeof obj2) return false;

  // Caso 3: Comparación de null/undefined
  if (obj1 == null || obj2 == null) return obj1 === obj2;

  // Caso 4: Comparación de fechas
  if (obj1 instanceof Date && obj2 instanceof Date) {
    return obj1.getTime() === obj2.getTime();
  }

  // Caso 5: Comparación de arrays
  if (Array.isArray(obj1) && Array.isArray(obj2)) {
    if (obj1.length !== obj2.length) return false;
    return obj1.every((item, index) => deepCompare(item, obj2[index]));
  }

  // Caso 6: Comparación de objetos
  if (typeof obj1 === 'object' && typeof obj2 === 'object') {
    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);

    if (keys1.length !== keys2.length) return false;

    return keys1.every(key =>
      deepCompare(obj1[key], obj2[key])
    );
  }

  // Caso 7: Otros tipos (number, string, boolean, etc.)
  return obj1 === obj2;
}

/** Se usa en los nzSelects para comparar objetos que no tienen la misma referencia **/
type Selectable = number | { id: number };
export const compareById = (a: Selectable | null, b: Selectable | null): boolean => {
  const aId = typeof a === 'number' ? a : a?.id;
  const bId = typeof b === 'number' ? b : b?.id;

  return aId === bId;
};

interface EnumSelectable {
  enum: string;
  // otras propiedades...
}

export const compareByEnum = (
  a: EnumSelectable | null,
  b: EnumSelectable | null
): boolean => {
  if (a === null && b === null) return true;
  if (a === null || b === null) return false;
  return a.enum === b.enum;
};

/** Se utiliza para eliminar porpiedades que tienen el valor de 'null', 'undefined' o un string vacio **/
export function deepClean<T>(obj: T): T {
  if (!obj || typeof obj !== "object") return obj;

  const cleanObj: any = Array.isArray(obj) ? [] : {};

  for (const [key, value] of Object.entries(obj)) {
    if (value !== null && value !== undefined && value !== "") {
      cleanObj[key] = typeof value === "object" ? deepClean(value) : value;

      // Eliminar objetos/arrays vacíos resultantes
      if (typeof cleanObj[key] === "object" && Object.keys(cleanObj[key]).length === 0) {
        delete cleanObj[key];
      }
    }
  }

  return Array.isArray(obj) ? cleanObj.filter(Boolean) : cleanObj;
}

/** Se utiliza para saber que tipo de dato se esta modificando en el componente ListKeyValueComponent **/
export function detectInterfaceType(obj: Record<string, any>): 'Equipment' | 'Document' | 'Service' | 'Project' | 'ServiceOrder' | 'LoadUnit' | 'ServiceOrderTemplate' | 'ServicePricing' | 'SaleOrder' | null {
  // Mapa de propiedades únicas representativas para cada interfaz (Pueden variar dependiendo de cuantas interfaces se comparen y varien las propiedades unicas de cada una)
  const interfaceKeys = {
    LoadUnit: ['batch'], // validationStatus solo en Document
    Document: ['fileExtension'], // validationStatus solo en Document
    Service: ['transportTypeId'], // service solo en Service
    ServiceOrder: ['mojitoIntegrationStatus'], // mojitoIntegrationStatus solo en ServiceOrder
    ServiceOrderTemplate: ['storageId'], // storageId solo en ServiceOrderTemplate
    Equipment: ['ownerId'], // ownerId solo existe en Equipment
    Project: ['reference'], // reference solo en Project
    ServicePricing: ['totalPrice'], // totalPrice solo en ServicePricing
    SaleOrder: ['quotationLines', 'budgetDate'] // quotationLines o budgetDate solo en SaleOrder (presupuesto)
  };

  // Verificar cada tipo
  for (const [type, keys] of Object.entries(interfaceKeys)) {
    if (keys.some(key => Object.keys(obj).includes(key))) {
      return type as 'Equipment' | 'Document' | 'Service' | 'Project' | 'ServiceOrder' | 'ServicePricing' | 'SaleOrder' | null;
    }
  }

  return null;
}

export function getItemById(id: number, list: any[]): any {
  return list.find(item => item.id === id);
}

export function getPartnerOptions(column: TableColumn): Partner[] {
  return column.optionList as Partner[];
}

export function isExpirationAlert(expirationDate: string): { alertState: 'nearExp' | 'expired' | undefined } {
  // Normalizar fechas: eliminar componente horario
  const normalizeDate = (date: Date): Date => {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  };

  const today = normalizeDate(new Date());
  const expiration = normalizeDate(new Date(expirationDate));

  // Calcular diferencia en días (entero)
  const diffTime = expiration.getTime() - today.getTime();
  const diffDays = Math.round(diffTime / (1000 * 3600 * 24));

  // Determinar estado según condiciones
  if (diffDays < 0) {
    return {alertState: 'expired'};
  } else if (diffDays <= 30) {
    return {alertState: 'nearExp'};
  } else {
    return {alertState: undefined};
  }
}

export function objectPropertiesToCamelCase(obj: any): any {
  if (typeof obj !== 'object' || obj === null) return obj;

  if (Array.isArray(obj)) {
    return obj.map(item => objectPropertiesToCamelCase(item));
  }

  return Object.fromEntries(
    Object.entries(obj).map(([key, value]) => [
      // Solo primera letra a minúscula, mantiene el resto
      key.charAt(0).toLowerCase() + key.slice(1),
      // Aplicar recursivamente a objetos/arrays
      objectPropertiesToCamelCase(value)
    ])
  );
}
